import { ArrowLeftOutlined } from '@ant-design/icons';
import { App as AntApp, Button, Form, Skeleton, Space, Tabs, Tag, Typography } from 'antd';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProvider, useSaveProvider, useRegisterWebhook, useProviderModels } from '../api/hooks';
import type { Provider } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { ProviderStatusTag } from '../utils/status';
import {
  mergeWebhookSecret,
  mergePollingByCategory,
  mergeRateLimit,
  mergeMaxConcurrent,
  pollingListFromConfig,
  rateLimitFromConfig,
  maxConcurrentFromConfig,
  stripManagedConfig,
  parseJsonText,
  toJsonText,
} from '../utils/format';
import ProviderFormFields from '../components/provider/ProviderFormFields';
import ProviderModelsTab from '../components/provider/ProviderModelsTab';
import ErrorRulesTab from '../components/provider/ErrorRulesTab';
import ModelTestTab from '../components/provider/ModelTestTab';
import SubSourcesTab from '../components/provider/SubSourcesTab';
import SubSourceTestTab from '../components/provider/SubSourceTestTab';
import { usePageHeader } from '../components/PageHeader';

const { Title } = Typography;

export default function ProviderDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const provider = useProvider(id);
  const models = useProviderModels(id);
  const save = useSaveProvider();
  const registerWebhook = useRegisterWebhook(id);
  const [form] = Form.useForm();
  // Gợi ý danh mục cho cấu hình polling (lấy từ model của nhà cung cấp này).
  const pollingCategories = Array.from(
    new Set((models.data ?? []).map((m) => m.category).filter((c): c is string => !!c)),
  );

  const onRegisterWebhook = async () => {
    try {
      const res = await registerWebhook.mutateAsync();
      message.success(`Đã đăng ký webhook với Plenxai (id=${res.webhookId})`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    if (provider.data) {
      // Tach webhookSecret + polling ra o rieng (khong hien trong JSON config de tranh trung/xung dot).
      const cfg = provider.data.config as Record<string, unknown> | undefined;
      const webhookSecret = cfg && typeof cfg === 'object' ? (cfg.webhookSecret as string | undefined) : undefined;
      form.setFieldsValue({
        ...provider.data,
        webhookSecret,
        rateLimitPerMin: rateLimitFromConfig(cfg),
        maxConcurrent: maxConcurrentFromConfig(cfg),
        pollingRows: pollingListFromConfig(cfg),
        headers: toJsonText(provider.data.headers),
        config: toJsonText(stripManagedConfig(cfg)),
      });
    }
  }, [provider.data, form]);

  const onSave = async () => {
    const v = await form.validateFields();
    const headers = parseJsonText(v.headers ?? '');
    const config = parseJsonText(v.config ?? '');
    const configValue = mergeMaxConcurrent(
      mergeRateLimit(
        mergePollingByCategory(mergeWebhookSecret(config.value, v.webhookSecret), v.pollingRows),
        v.rateLimitPerMin,
      ),
      v.maxConcurrent,
    );
    const payload: Partial<Provider> = {
      slug: v.slug,
      name: v.name,
      description: v.description,
      category: v.category,
      status: v.status,
      version: v.version,
      logo: v.logo,
      baseUrl: v.baseUrl,
      authType: v.authType,
      apiKey: v.apiKey,
      headers: headers.value as Record<string, unknown> | undefined,
      timeoutMs: v.timeoutMs,
      retryCount: v.retryCount,
      submitEndpoint: v.submitEndpoint,
      statusEndpoint: v.statusEndpoint,
      cancelEndpoint: v.cancelEndpoint,
      adapter: v.adapter,
      executionMode: v.executionMode,
      config: configValue,
    };
    try {
      await save.mutateAsync({ id, data: payload });
      message.success('Đã lưu nhà cung cấp');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  // Bơm cụm tiêu đề (nút back + tên + slug + trạng thái) lên header toàn cục.
  const d = provider.data;
  usePageHeader(
    d ? (
      <Space align="center" size={12}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/providers')} />
        <Title level={4} style={{ margin: 0 }}>
          {d.name}
        </Title>
        <Tag>{d.slug}</Tag>
        <ProviderStatusTag status={d.status} />
      </Space>
    ) : null,
    [d?.id, d?.name, d?.slug, d?.status],
  );

  if (provider.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!provider.data) return <Typography.Text>Không tìm thấy nhà cung cấp.</Typography.Text>;

  const isVilao = provider.data?.adapter === 'vilao' || provider.data?.adapter === 'gpt-smart';
  const isFlipAi = provider.data?.adapter === 'flipai';
  // Adapter webhook ĐĂNG KÝ TOÀN CỤC -> hiện nút "Đăng ký webhook ngay".
  const isGlobalWebhook =
    provider.data?.adapter === 'plenxai-free-webhook' || provider.data?.adapter === 'gpt-playwright-webhook';
  const registeredWebhookId = (provider.data?.config as Record<string, unknown> | undefined)?.webhookId as
    | string
    | undefined;
  const infoTab = (
    <Form form={form} layout="vertical">
      <ProviderFormFields pollingCategories={pollingCategories} />
      <Space style={{ marginTop: 16 }} wrap>
        <Button type="primary" loading={save.isPending} onClick={onSave}>
          Lưu thay đổi
        </Button>
        {isGlobalWebhook && (
          <>
            <Button loading={registerWebhook.isPending} onClick={onRegisterWebhook}>
              Đăng ký webhook ngay
            </Button>
            <Typography.Text type={registeredWebhookId ? 'success' : 'secondary'}>
              {registeredWebhookId ? `Webhook đã đăng ký (id=${registeredWebhookId})` : 'Webhook chưa đăng ký'}
            </Typography.Text>
          </>
        )}
      </Space>
    </Form>
  );

  // Vilao dùng NGUỒN CON thay cho danh sách Model. FlipAI vẫn giữ Model và có thêm
  // tab API con để gộp/xoay số luồng của nhiều credential.
  const tabs = [
    { key: 'info', label: 'Thông tin', children: infoTab },
    ...(isVilao
      ? [{ key: 'subsources', label: 'Nguồn con', children: <SubSourcesTab providerId={id} /> }]
      : [
          { key: 'models', label: 'Model', children: <ProviderModelsTab providerId={id} /> },
          ...(isFlipAi
            ? [
                {
                  key: 'subsources',
                  label: 'API con',
                  children: <SubSourcesTab providerId={id} variant="flipai" />,
                },
              ]
            : []),
        ]),
    { key: 'rules', label: 'Quy tắc lỗi (Error Rules)', children: <ErrorRulesTab providerId={id} /> },
    {
      key: 'test',
      label: 'Kiểm thử',
      children: isVilao ? <SubSourceTestTab providerId={id} /> : <ModelTestTab providerId={id} />,
    },
  ];

  return (
    <div>
      <Tabs items={tabs} />
    </div>
  );
}
